// Contingency Plans: emergency checklists (contingency-plans.json -> the shared Hey Captain library, sql/contingency-library.sql;
// each vessel adopts a copy: scripts/import-contingency.mjs or the Checklists page) kept as ordinary vessel checklists
// (checklists + checklist_items, category "Contingency Plans") and run in the emergency mode of the
// Checklists page (src/components/EmergencyRun.jsx). Plain JS, no imports from the app: the page, the importer, the AI tools
// (api/_contingency.js) and the tests (tests/contingency.test.mjs) all use it.
//   template:  title, description "… Forms to record: Incident Report. Record in: Official Log Book …" (editable like any
//              checklist description: the forms / log books of a run are read back from it), steps = check items
//   run:       { id, checklistId, title, startedAt, startedBy, tz, steps[], marks: { [i]: { status, at, by, reason? } }, events[] }
//              kept on the device while it runs (works offline), saved on finish as one checklist_instances row with the
//              timeline (sql/contingency.sql) + one draft per form and per log book (emergency_records, never signed)
import { uniformRows, THUNDER } from './raLibrary.js'
import { registerLibrary } from './libraryAdd.js'
export { uniformRows, THUNDER }
export const CONTINGENCY_CATEGORY = 'Contingency Plans'
export const DEFAULT_FORMS = ['Incident Report']
export const DEFAULT_LOG = 'Official Log Book'
export const UNDO_MS = 30000          // an AI tick can be undone for 30 s
export const RUN_KEY = 'hc_emergency_run'      // the run in progress on this device (localStorage)
export const START_KEY = 'hc_emergency_start'  // "start this plan" handed to the Checklists page (localStorage)

const text = v => String(v == null ? '' : v).replace(/\s+/g, ' ').trim()
const nrm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
export const isContingency = c => !!c && text(c.category) === CONTINGENCY_CATEGORY
export const sortPlans = list => (list || []).slice().sort((a, b) => text(a.title).localeCompare(text(b.title), 'en', { numeric: true, sensitivity: 'base' }))
// a check step (not a header / note / attendance / signature / drill type): what is ticked or skipped
export const isCheckStep = it => !!it && !it.is_header && !it.is_note && !it.is_attendance && !it.is_signature && !it.is_drill_type
export const stepCount = c => (c && c.checklist_items ? c.checklist_items.filter(isCheckStep).length : 0)

// "Defect Report, Incident Report" / ["Incident Report"] / "None" -> ['Defect Report', 'Incident Report'] / []
export function splitList(v) {
  return (Array.isArray(v) ? v : [v]).flatMap(x => String(x == null ? '' : x).split(/\s*[,;]\s*/)).map(text).filter(x => x && !/^(none|n\/a|-)$/i.test(x))
}
// "Official Log Book and onboard management system" -> ['Official Log Book']; "Official Log Book and Deck Log Book" -> both
export function logBooksOf(recordIn) {
  const s = splitList(recordIn).join(' and ')
  const out = []
  for (const m of s.matchAll(/\b(official|deck|engine(?: room)?|oil record|garbage record)\s+(log ?book|record book)\b/gi)) {
    const name = m[0].replace(/\s+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/Logbook/i, 'Log Book')
    if (!out.includes(name)) out.push(name)
  }
  return out
}

// ---------------------------------------------------------------- the file -> templates
export function validatePlans(file) {
  const bad = []
  const list = file && Array.isArray(file.checklists) ? file.checklists : null
  if (!list) return ['no "checklists" list']
  if (file.category && text(file.category) !== CONTINGENCY_CATEGORY) bad.push('category is "' + file.category + '", expected "' + CONTINGENCY_CATEGORY + '"')
  const seen = new Set()
  list.forEach((p, i) => {
    const w = (p && p.title ? p.title : '#' + (i + 1)) + ': '
    if (!text(p && p.title)) bad.push(w + 'no title')
    if (seen.has(text(p.title).toLowerCase())) bad.push(w + 'duplicate title')
    seen.add(text(p.title).toLowerCase())
    if (!Array.isArray(p.steps) || !p.steps.map(text).filter(Boolean).length) bad.push(w + 'no steps')
    if (p.forms_to_record != null && !Array.isArray(p.forms_to_record) && typeof p.forms_to_record !== 'string') bad.push(w + 'forms_to_record must be a list')
    if (p.record_in != null && !Array.isArray(p.record_in) && typeof p.record_in !== 'string') bad.push(w + 'record_in must be a list')
  })
  return bad
}
// the description carries what a run records, in words the vessel can edit
export function describePlan(p) {
  const forms = splitList(p.forms_to_record), rec = splitList(p.record_in)
  const extra = (p.other_lines || []).map(text).filter(x => x && nrm(x) !== nrm(p.title))
  return ['Emergency contingency plan.', 'Forms to record: ' + (forms.length ? forms.join(', ') : 'None') + '.', 'Record in: ' + (rec.length ? rec.join('; ') : DEFAULT_LOG) + '.', ...extra].join(' ')
}
// read back from a (possibly edited) description: { forms, logBooks }; no "Forms to record" line = Incident Report
export function recordingOf(description) {
  const d = String(description || '')
  const f = d.match(/forms? to record:\s*([^.\n]*)/i), r = d.match(/record in:\s*([^.\n]*)/i)
  const forms = f ? splitList(f[1]) : DEFAULT_FORMS.slice()
  const books = r ? logBooksOf(r[1].split(/\s*;\s*/)) : []
  return { forms, logBooks: books.length ? books : [DEFAULT_LOG] }
}
const STEP_DEFAULTS = { required: true, is_header: false, is_note: false, is_attendance: false, is_signature: false, is_drill_type: false }
export function mapPlan(p) {
  return {
    title: text(p.title), description: describePlan(p), category: CONTINGENCY_CATEGORY, source: LIBRARY_SOURCE,
    items: uniformRows((p.steps || []).map(text).filter(Boolean).map((t, i) => ({ ...STEP_DEFAULTS, step_order: i + 1, title: t }))),
  }
}
// ---------------------------------------------------------------- the Hey Captain library (shared by every vessel)
// The plans are kept ONCE, with no vessel: checklists rows with client_id null, source 'library', is_template false (so the
// template clone for new / empty vessels does not copy them). Every login reads them; a vessel ADOPTS a plan = its own copy
// (source 'library', library_id = the library row), which it edits like any checklist (sql/contingency-library.sql).
export const LIBRARY_SOURCE = 'library'
export const isLibraryPlan = c => !!c && c.client_id == null && c.source === LIBRARY_SOURCE
// the plans file -> sql/contingency-library.sql (scripts/contingency-library-sql.mjs). The data goes in as one jsonb literal;
// the SQL adds a plan only when the library has no plan with that title + category, and steps only to a plan without any
export function libraryData(file) {
  return (file.checklists || []).map(p => { const m = mapPlan(p); return { title: m.title, description: m.description, category: m.category, steps: m.items.map(i => i.title) } })
}
export function libraryJson(file) {
  const j = JSON.stringify(libraryData(file), null, 1)
  if (j.includes('$plans$')) throw new Error('the plans contain "$plans$" - cannot be quoted')
  return j
}
// the library kind for the shared picker / engine (src/components/LibraryPicker.jsx, src/lib/libraryAdd.js): the library is read
// from the database (loadContingencyLibrary), the vessel's rows are its checklists (with their step count), and an entry is
// written by public.adopt_library_checklist (one call: the copy, its link and its steps - never twice)
const plainKey = (t, c) => text(t).toLowerCase() + '|' + text(c).toLowerCase()
const stepsOf = r => (Array.isArray(r.checklist_items) && r.checklist_items[0] && 'count' in r.checklist_items[0] ? Number(r.checklist_items[0].count) : Array.isArray(r.checklist_items) ? r.checklist_items.length : Number(r.items || 0))
export const CONTINGENCY_LIBRARY = registerLibrary({
  key: 'contingency_plans', label: 'contingency plans', table: 'checklists', verb: 'Adopt', createdBy: 'Hey Captain library', setupSql: 'sql/contingency-library.sql',
  addedNote: 'each is now the vessel\'s own copy - review and adapt it',
  existingSelect: 'id,title,category,library_id,checklist_items(count)',
  items: lib => sortPlans(Array.isArray(lib) ? lib.filter(isLibraryPlan) : []),
  itemKey: l => [l.id, l.title], rowKey: r => [r.library_id || '', r.title],
  // linked copy first, else a plan of the same title + section (kept as it is and linked by the adopt)
  match: (l, rows) => rows.find(r => r.library_id === l.id) || rows.find(r => !r.library_id && plainKey(r.title, r.category) === plainKey(l.title, l.category)) || null,
  unfinished: r => (stepsOf(r) === 0 ? 'its copy has no steps - adopt again to finish it' : !r.library_id ? 'a plan of this title is on the vessel - adopt links it (kept as it is)' : false),
  map: () => ({ row: null, checklist: null }),
  add: (api, p, clientId) => api.rpc('adopt_library_checklist', { target: clientId, p_library: p.item.id }),
  card: l => { const r = recordingOf(l.description); return { code: '', title: text(l.title), lines: [stepCount(l) + ' steps \u00b7 records: ' + (r.forms.length ? r.forms.join(', ') : 'no form') + ' \u00b7 ' + r.logBooks.join(', ')] } },
  preview: l => (l.checklist_items || []).filter(isCheckStep).sort((a, b) => a.step_order - b.step_order).map(i => i.title),
})

// ---------------------------------------------------------------- finding a plan from words ("man overboard", "fire in the engine room")
// each rule: words in the question -> plan title words (normalised, a plan title containing them matches), best first
const PLAN_WORDS = [
  [/\b(man|person|crew( ?member)?|guest|child|someone|somebody|passenger)\s+(is\s+|went\s+|has\s+gone\s+|gone\s+|fell\s+)?overboard\b|\bmob\b|\bfell (in|into) the (water|sea)\b/i, ['mobimmediateaction', 'mobdelayedaction', 'mobmissingperson']],
  [/\bmissing (person|crew|guest)\b|\bcan'?t find\b|\bunaccounted\b/i, ['mobmissingperson']],
  [/\bfire\b|\bsmoke\b|\bburning\b|\bflames?\b/i, ['fireatsea', 'fireinport']],
  [/\bflood|\bwater (coming )?in\b|\btaking (on )?water\b|\bhull (breach|failure|crack)|\bleak(ing)?\b|\bsinking\b/i, ['floodingorhullfailure']],
  [/\bcollision|\bcollided|\bhit (a|another|the) (vessel|boat|ship|yacht)|\ballision\b/i, ['collision']],
  [/\baground|\bgrounding|\bgrounded|\bran onto|\bhit (a )?(rock|reef|sandbank|bottom)\b/i, ['grounding']],
  [/\babandon(ing)? ship\b|\babandon\b/i, ['abandonship']],
  [/\b(heli|helicopter|medevac|medivac|evacuat)/i, ['medicalevacuationbyhelicopter']],
  [/\bmedical\b|\binjur|\bunconscious\b|\bheart attack\b|\bnot breathing\b|\bcollapsed\b|\bbleeding\b|\bstroke\b|\bseizure\b|\bcasualty\b|\bburns?\b/i, ['medicalemergency']],
  [/\b(dead|death|died|deceased|fatality|fatal)\b/i, ['fatality']],
  [/\benclosed space\b|\bconfined space\b|\b(in|inside) (a|the) tank\b|\bvoid space\b|\bchain locker\b/i, ['enclosedspacerescue']],
  [/\bpira(tes?|cy)\b|\barmed (men|boarding|attack)\b|\bhijack\b|\bboarded\b/i, ['piracy']],
  [/\b(oil|fuel|diesel) (spill|leak)\b|\bpollution\b|\bspill\b|\bsheen\b/i, ['pollutionincident']],
  [/\bhurricane\b|\bcyclone\b|\btyphoon\b|\btropical storm\b|\bstorm\b/i, ['hurricane']],
  [/\bdismast|\bmast (came down|broke|failed|down)\b|\brig(ging)? failure\b/i, ['dismasting']],
  [/\b(lost|loss of|no) (propulsion|steering|engines?)\b|\bsteering (failure|failed|gone)\b|\bengine(s)? (failure|failed|stopped|died)\b|\bnot under command\b/i, ['propulsionorsteeringfailure']],
  [/\bblack ?out\b|\bpower failure\b|\bgenerator (failure|failed|tripped)\b|\bequipment failure\b/i, ['criticalequipmentfailure']],
  [/\b(need|needs|request|get) (a )?tow\b|\bemergency tow|\btowing\b|\bsalvage\b|\blof\b/i, ['emergencytowing']],
]
// at sea / in port decides between Fire at Sea and Fire in Port when the words say which
const portWords = /\b(in port|alongside|in the marina|at the dock|berthed|moored|in the yard|shipyard|at anchor in port)\b/i
// plans = the vessel's contingency checklists [{ id, title, ... }]; -> the matching plans, best first ([] = no match)
export function matchPlans(plans, query) {
  const q = String(query || '')
  const list = sortPlans(plans)
  const byWord = w => list.filter(p => nrm(p.title).includes(w))
  const out = []
  const add = p => { if (p && !out.includes(p)) out.push(p) }
  for (const [re, words] of PLAN_WORDS) {
    if (!re.test(q)) continue
    let ws = words
    if (words[0] === 'fireatsea' && portWords.test(q)) ws = ['fireinport', 'fireatsea']
    for (const w of ws) byWord(w).forEach(add)
  }
  // the plan named (or most of its words): "abandon ship checklist", "enclosed space rescue"
  const qn = nrm(q)
  for (const p of list) { const t = nrm(String(p.title).replace(/\bchecklist\b/i, '')); if (t.length >= 4 && (qn.includes(t) || (qn.length >= 5 && t.includes(qn)))) add(p) }
  if (!out.length) {
    const words = (q.toLowerCase().match(/[a-z]{4,}/g) || []).filter(w => !/^(emergency|checklist|plan|open|start|have|there|what|with|this|that|help|please|need|onboard|board|the)$/.test(w))
    const scored = list.map(p => ({ p, n: words.filter(w => nrm(p.title).includes(w)).length })).filter(x => x.n).sort((a, b) => b.n - a.n)
    scored.forEach(x => add(x.p))
  }
  return out
}

// ---------------------------------------------------------------- ship's time
// 'HH:MM:SS' of an instant in the ship's time zone (config.timezone, src/lib/shipTime.js) - never the device's zone
export function shipClock(at, tz) {
  const d = at instanceof Date ? at : new Date(at)
  if (isNaN(d)) return ''
  try { return new Intl.DateTimeFormat('en-GB', { timeZone: tz || 'UTC', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(d) }
  catch (e) { return d.toISOString().slice(11, 19) }
}
export function shipDate(at, tz) {
  const d = at instanceof Date ? at : new Date(at)
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d) } catch (e) { return d.toISOString().slice(0, 10) }
}
export const tzLabel = tz => tz || 'UTC'
export function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60
  const p = n => String(n).padStart(2, '0')
  return h ? h + ':' + p(m) + ':' + p(x) : p(m) + ':' + p(x)
}
export function elapsedWords(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60
  const part = (n, w) => n + ' ' + w + (n === 1 ? '' : 's')
  return h ? part(h, 'hour') + ' ' + part(m, 'minute') : m ? part(m, 'minute') + (x ? ' ' + part(x, 'second') : '') : part(x, 'second')
}

// ---------------------------------------------------------------- the run
const iso = at => (at instanceof Date ? at : new Date(at == null ? Date.now() : at)).toISOString()
// mode 'drill' (src/lib/drillPlans.js): the same run, saved as a drill (attendance, debrief, no Incident Report); drillType = the drill type label
export function newRun({ checklist, who, at, tz, id, mode, drillType }) {
  if (!checklist || !checklist.id) throw new Error('No plan to start')
  const steps = (checklist.checklist_items || []).map((it, i) => ({ i, title: text(it.title), check: isCheckStep(it), header: !!it.is_header }))
  if (!steps.some(s => s.check)) throw new Error('This plan has no steps')
  const rec = recordingOf(checklist.description)
  return { id: id || null, checklistId: checklist.id, title: text(checklist.title), mode: mode === 'drill' ? 'drill' : 'emergency', drillType: mode === 'drill' ? (text(drillType) || null) : null, startedAt: iso(at), startedBy: text(who) || 'Crew', tz: tz || null, forms: rec.forms, logBooks: rec.logBooks, steps, marks: {}, events: [], finishedAt: null }
}
const checkIdx = run => run.steps.filter(s => s.check).map(s => s.i)
const mustCheck = (run, i) => { if (!run || !run.steps[i] || !run.steps[i].check) throw new Error('No such step') }
const log = (run, ev) => [...(run.events || []), ev]
// tick a step: who and when (a step already ticked or skipped stays as it was: the first time counts)
export function tick(run, i, who, at) {
  mustCheck(run, i)
  if (run.finishedAt) throw new Error('This run is finished')
  if (run.marks[i]) return run
  const m = { status: 'done', at: iso(at), by: text(who) || 'Crew' }
  return { ...run, marks: { ...run.marks, [i]: m }, events: log(run, { type: 'done', i, at: m.at, by: m.by }) }
}
// "Skip / not applicable": a reason is required
export function skip(run, i, reason, who, at) {
  mustCheck(run, i)
  if (run.finishedAt) throw new Error('This run is finished')
  const r = text(reason)
  if (!r) throw new Error('Say why the step is skipped / not applicable')
  if (run.marks[i]) return run
  const m = { status: 'skipped', at: iso(at), by: text(who) || 'Crew', reason: r.slice(0, 300) }
  return { ...run, marks: { ...run.marks, [i]: m }, events: log(run, { type: 'skipped', i, at: m.at, by: m.by, reason: m.reason }) }
}
// undo a tick / skip (kept in the events, so the history shows it was undone)
export function undo(run, i, who, at) {
  mustCheck(run, i)
  if (run.finishedAt || !run.marks[i]) return run
  const marks = { ...run.marks }; delete marks[i]
  return { ...run, marks, events: log(run, { type: 'undone', i, at: iso(at), by: text(who) || 'Crew' }) }
}
export const openSteps = run => checkIdx(run).filter(i => !run.marks[i])
export function nextOpen(run, from = 0) { const o = openSteps(run); return o.find(i => i >= from) ?? (o.length ? o[0] : -1) }
export const stepNumber = (run, i) => checkIdx(run).indexOf(i) + 1
export const totalSteps = run => checkIdx(run).length
export const elapsedMs = (run, now) => Math.max(0, new Date(run.finishedAt || now || Date.now()) - new Date(run.startedAt))
export const speakStep = (run, i) => i < 0 ? 'All steps are done or skipped. Finish the run when ready.' : 'Step ' + stepNumber(run, i) + ' of ' + totalSteps(run) + '. ' + run.steps[i].title
// a saved run (checklist_instances row with its timeline) + its plan -> the run again (summary / drafts after the event)
export function runFromInstance(inst, checklist) {
  const tl = (inst && inst.timeline) || {}
  const run = newRun({ checklist, who: inst.started_by || tl.started_by, at: inst.started_at || tl.started_at, tz: tl.tz && tl.tz !== 'UTC' ? tl.tz : null, id: inst.id })
  const marks = {}
  for (const e of tl.steps || []) if (run.steps[e.i] && run.steps[e.i].check) marks[e.i] = { status: e.status === 'skipped' ? 'skipped' : 'done', at: e.at, by: e.by, ...(e.reason ? { reason: e.reason } : {}) }
  return { ...run, marks, events: tl.events || [], finishedAt: inst.completed_at || tl.finished_at || null }
}
// the timeline: every step ticked / skipped, in the order it happened, with ship's time and who
export function timeline(run) {
  return Object.entries(run.marks).map(([i, m]) => ({ n: stepNumber(run, Number(i)), i: Number(i), step: run.steps[i].title, status: m.status, at: m.at, ship: shipClock(m.at, run.tz), by: m.by, ...(m.reason ? { reason: m.reason } : {}) }))
    .sort((a, b) => a.at.localeCompare(b.at) || a.n - b.n)
}
export const timelineLine = e => e.ship + '  ' + (e.status === 'skipped' ? 'SKIPPED ' : '') + 'Step ' + e.n + ': ' + e.step + ' - ' + e.by + (e.reason ? ' (not applicable: ' + e.reason + ')' : '')
export function summary(run, now) {
  const tl = timeline(run), open = openSteps(run)
  return {
    title: run.title, started: shipClock(run.startedAt, run.tz), started_by: run.startedBy, tz: tzLabel(run.tz), elapsed: fmtElapsed(elapsedMs(run, now)),
    done: tl.filter(e => e.status === 'done').length, skipped: tl.filter(e => e.status === 'skipped').length, open: open.length, total: totalSteps(run),
  }
}
// the note kept on each step of the saved run (shown by the run history, the PDF and the exports as they are)
export const stepNote = m => (m.status === 'skipped' ? 'Skipped ' : 'Done ') + m.ship + ' · ' + m.by + (m.reason ? ' — not applicable: ' + m.reason : '')
// the checklist_instances row of a finished run. timeline / emergency are columns of sql/contingency.sql (the page leaves
// them out and saves again when that file is not run yet); create_snags = false: a skipped step is not a defect
export function toInstance(run, { clientId, finishedAt, generalNote } = {}) {
  const fin = iso(finishedAt || run.finishedAt || Date.now())
  const r = { ...run, finishedAt: fin }
  const tl = timeline(r), s = summary(r)
  const step_completion = {}, step_notes = {}
  r.steps.forEach(st => { if (st.check) step_completion[st.i] = !!(r.marks[st.i] && r.marks[st.i].status === 'done') })
  for (const e of tl) step_notes[e.i] = stepNote(e)
  const head = 'EMERGENCY RUN - ' + r.title + '. Started ' + s.started + ' by ' + s.started_by + ', finished ' + shipClock(fin, r.tz) + " (ship's time, " + s.tz + '), elapsed ' + s.elapsed + '. ' + s.done + ' done, ' + s.skipped + ' skipped, ' + s.open + ' not done of ' + s.total + '.'
  return {
    checklist_id: r.checklistId, client_id: clientId, started_by: r.startedBy, started_at: r.startedAt, completed_at: fin,
    step_completion, step_notes, general_notes: head + (text(generalNote) ? '\n' + String(generalNote).trim() : ''),
    create_snags: false, emergency: true,
    timeline: { tz: s.tz, started_at: r.startedAt, started_by: r.startedBy, finished_at: fin, elapsed: s.elapsed, steps: tl, events: r.events },
  }
}
// the drafts made when a run is finished: one per form named in the plan ("Incident Report", "Defect Report", "LOF 2020") and one
// entry per log book ("Official Log Book", "Deck Log Book"), each pre-filled with the timeline. status 'draft', never signed
// (the database keeps them unsigned on insert too: sql/contingency.sql emergency_records_guard)
export function draftRecords(run, { clientId, runId, finishedAt, vesselName, createdBy } = {}) {
  const fin = finishedAt || run.finishedAt || new Date().toISOString()
  const r = { ...run, finishedAt: fin }
  const tl = timeline(r), s = summary(r)
  const day = shipDate(r.startedAt, r.tz)
  const lines = tl.map(timelineLine)
  const open = openSteps(r).map(i => '  Step ' + stepNumber(r, i) + ': ' + r.steps[i].title)
  const facts = [
    (vesselName ? 'Vessel: ' + vesselName + '\n' : '') + 'Emergency: ' + r.title,
    'Date: ' + day + " · Started " + s.started + ' by ' + s.started_by + ' · Finished ' + shipClock(fin, r.tz) + " · Elapsed " + s.elapsed + " (ship's time, " + s.tz + ')',
    'Steps: ' + s.done + ' done, ' + s.skipped + ' skipped, ' + s.open + ' not done (of ' + s.total + ')',
  ].join('\n')
  const body = (what) => facts + '\n\nTimeline:\n' + (lines.length ? lines.map(l => '  ' + l).join('\n') : '  (no step recorded)') + (open.length ? '\n\nNot done:\n' + open.join('\n') : '') + '\n\n' + what
  const base = { client_id: clientId, run_id: runId || null, checklist_id: r.checklistId, status: 'draft', created_by: text(createdBy) || r.startedBy, timeline: tl }
  const rows = []
  for (const f of r.forms || []) rows.push({ ...base, kind: 'form', form_name: f, log_book: null, title: f + ' - ' + r.title.replace(/\s+checklist$/i, '') + ' - ' + day,
    body: body('Description of the incident, people involved, injuries / damage, actions taken and lessons learned: to be completed.\nDRAFT - review, complete and sign.') })
  for (const b of r.logBooks && r.logBooks.length ? r.logBooks : [DEFAULT_LOG]) rows.push({ ...base, kind: 'log_entry', form_name: null, log_book: b, title: b + ' entry - ' + r.title.replace(/\s+checklist$/i, '') + ' - ' + day,
    body: body('Position, weather and further remarks: to be completed.\nDRAFT ' + b + ' entry - to be checked and signed by the Master.') })
  return uniformRows(rows)
}

// ---------------------------------------------------------------- risk assessments -> their emergency arrangements
// words in the RA (activity / scope) -> plan title words, in order: SUGGESTIONS for the form only (raSuggestedPlans), never a pick
const RA_PLANS = [
  [/enclosed space|confined space/i, ['enclosedspacerescue']],
  [/rescue boat|man overboard|overside|working over ?the ?side/i, ['mobimmediateaction']],
  [/diving|diver|snorkel/i, ['mobmissingperson', 'medicalemergency']],
  [/aloft|at height|working at height/i, ['medicalemergency']],
  [/crane|davit/i, ['medicalemergency', 'mobimmediateaction']],
  [/mooring/i, ['medicalemergency', 'mobimmediateaction']],
  [/anchor/i, ['grounding', 'medicalemergency']],
  [/one generator|single generator|generator/i, ['criticalequipmentfailure', 'propulsionorsteeringfailure']],
  [/alarm and monitoring|alarm system|monitoring system/i, ['fireatsea', 'floodingorhullfailure', 'criticalequipmentfailure']],
  [/additional person|extra person|persons? on board/i, ['abandonship']],
  [/hot work|welding|grinding/i, ['fireinport', 'fireatsea']],
  [/bunker|fuel transfer|refuel/i, ['pollutionincident', 'fireinport']],
  [/helicopter|helideck/i, ['medicalevacuationbyhelicopter']],
  [/emergency tow/i, ['emergencytowing']],
]
// the RA's emergency arrangements: ONLY the plans picked for it (emergency_plans [{ id, title }], sql/ra-visual.sql - several per RA,
// the vessel's current title; a plan since removed keeps its title, marked missing). Nothing is guessed: an RA with none picked
// (null or []) has none - on the list, the PDF, the Risk view and for the AI. ra = a vessel row (emergency_plans) or the form (emergencyPlans)
export function raPlans(ra, plans) {
  const r = ra || {}, picked = r.emergencyPlans !== undefined ? r.emergencyPlans : r.emergency_plans
  if (!Array.isArray(picked)) return []
  return picked.map(p => (p && (plans || []).find(x => x.id === p.id)) || (p && p.title ? { id: p.id, title: p.title, missing: true } : null)).filter(Boolean)
}
// SUGGESTIONS only: the plans the AI suggested for an AI draft (suggested_plans [{ id, title }], sql/fixes-1.sql) first, then the
// vessel's plans the activity / scope words point to - minus the ones already picked. The form shows them as "Suggested: MOB
// Immediate Action" for the Master to confirm with one click; they are never listed or printed (nor picked) until confirmed.
// -> [{ id, title, by: 'ai' | 'words' }] of the vessel's current plans
export function raSuggestedPlans(ra, plans) {
  const r = ra || {}, picked = r.emergencyPlans !== undefined ? r.emergencyPlans : r.emergency_plans
  const have = new Set((Array.isArray(picked) ? picked : []).map(p => p && p.id))
  const stored = r.suggestedPlans !== undefined ? r.suggestedPlans : r.suggested_plans
  const ai = (Array.isArray(stored) ? stored : []).map(p => p && (plans || []).find(x => x.id === p.id)).filter(Boolean)
  const out = []
  for (const [p, by] of [...ai.map(p => [p, 'ai']), ...raEmergencyPlans({ activity: r.activity != null ? r.activity : r.Activity, scope: r.scope }, plans).map(p => [p, 'words'])]) {
    if (!have.has(p.id) && !out.some(o => o.id === p.id)) out.push({ ...p, by })
  }
  return out
}
// the plans an AI draft suggests: titles the AI gave -> the vessel's plans of those titles [{ id, title }] (a title it made up: dropped)
export function plansFromTitles(titles, plans) {
  const out = []
  for (const t of Array.isArray(titles) ? titles : []) {
    const p = (plans || []).find(x => nrm(x.title) === nrm(t)) || (plans || []).find(x => nrm(t).length >= 6 && (nrm(x.title).includes(nrm(t)) || nrm(t).includes(nrm(x.title))))
    if (p && !out.some(o => o.id === p.id)) out.push({ id: p.id, title: p.title })
  }
  return out.slice(0, 4)
}
// the keyword rules behind the suggestions: ra = { activity, scope } -> [{ id, title }] of the vessel's plans (none = no match)
export function raEmergencyPlans(ra, plans) {
  const s = [ra && ra.activity, ra && ra.scope].map(x => String(x || '')).join(' ')
  const out = []
  for (const [re, words] of RA_PLANS) if (re.test(s)) for (const w of words) { const p = (plans || []).find(x => nrm(x.title).includes(w)); if (p && !out.includes(p)) out.push(p) }
  return out
}
